import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Chip, Alert } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import api from '../../services/api';

const statusLabels = {
  entwurf: { label: 'Entwurf', color: 'default' },
  in_bearbeitung: { label: 'In Bearbeitung', color: 'info' },
  eingereicht: { label: 'Eingereicht', color: 'primary' },
  bewilligt: { label: 'Bewilligt', color: 'success' },
  abgelehnt: { label: 'Abgelehnt', color: 'error' },
  rueckmeldung: { label: 'R\u00fcckmeldung', color: 'warning' },
};

const AntraegePage = () => {
  const [antraege, setAntraege] = useState([]);
  const [foerdergeber, setFoerdergeber] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    titel: '', beschreibung: '', foerdergeberId: '', foerderprogramm: '',
    status: 'entwurf', einreichungsfrist: '', beantragteSumme: '', laufzeitVon: '', laufzeitBis: ''
  });

  const load = async () => {
    try {
      const [aRes, fgRes] = await Promise.all([api.get('/antraege'), api.get('/foerdergeber')]);
      setAntraege(aRes.data);
      setFoerdergeber(fgRes.data);
    } catch (err) { setError('Fehler beim Laden'); }
  };

  useEffect(() => { load(); }, []);

  const handleOpen = (antrag = null) => {
    if (antrag) {
      setEditing(antrag);
      setForm({
        titel: antrag.titel || '', beschreibung: antrag.beschreibung || '',
        foerdergeberId: antrag.foerdergeberId || '', foerderprogramm: antrag.foerderprogramm || '',
        status: antrag.status || 'entwurf', einreichungsfrist: antrag.einreichungsfrist || '',
        beantragteSumme: antrag.beantragteSumme || '', laufzeitVon: antrag.laufzeitVon || '', laufzeitBis: antrag.laufzeitBis || ''
      });
    } else {
      setEditing(null);
      setForm({ titel: '', beschreibung: '', foerdergeberId: '', foerderprogramm: '',
        status: 'entwurf', einreichungsfrist: '', beantragteSumme: '', laufzeitVon: '', laufzeitBis: '' });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const data = { ...form };
      if (!data.beantragteSumme) delete data.beantragteSumme;
      if (!data.einreichungsfrist) delete data.einreichungsfrist;
      if (!data.laufzeitVon) delete data.laufzeitVon;
      if (!data.laufzeitBis) delete data.laufzeitBis;

      if (editing) {
        await api.put(`/antraege/${editing.id}`, data);
      } else {
        await api.post('/antraege', data);
      }
      setOpen(false);
      load();
    } catch (err) { setError('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Antrag wirklich l\u00f6schen?')) {
      try {
        await api.delete(`/antraege/${id}`);
        load();
      } catch (err) { setError('Fehler beim L\u00f6schen'); }
    }
  };

  const formatCurrency = (val) => val ? parseFloat(val).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '-';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="#1B5E20">Antr\u00e4ge</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}
          sx={{ bgcolor: '#1B5E20', '&:hover': { bgcolor: '#2E7D32' } }}>
          Neuer Antrag
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#E8F5E9' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Titel</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>F\u00f6rdergeber</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Beantragte Summe</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Frist</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {antraege.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#999' }}>Noch keine Antr\u00e4ge vorhanden</TableCell></TableRow>
            ) : antraege.map((a) => (
              <TableRow key={a.id} hover>
                <TableCell>{a.titel}</TableCell>
                <TableCell>{a.foerdergeber?.name || '-'}</TableCell>
                <TableCell><Chip label={statusLabels[a.status]?.label} color={statusLabels[a.status]?.color} size="small" /></TableCell>
                <TableCell>{formatCurrency(a.beantragteSumme)}</TableCell>
                <TableCell>{a.einreichungsfrist || '-'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpen(a)}><Edit /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(a.id)}><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Antrag bearbeiten' : 'Neuer Antrag'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Titel *" value={form.titel} onChange={(e) => setForm({...form, titel: e.target.value})} margin="normal" />
          <TextField fullWidth select label="F\u00f6rdergeber *" value={form.foerdergeberId} onChange={(e) => setForm({...form, foerdergeberId: e.target.value})} margin="normal">
            {foerdergeber.map((fg) => <MenuItem key={fg.id} value={fg.id}>{fg.name}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="F\u00f6rderprogramm" value={form.foerderprogramm} onChange={(e) => setForm({...form, foerderprogramm: e.target.value})} margin="normal" />
          <TextField fullWidth select label="Status" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} margin="normal">
            {Object.entries(statusLabels).map(([key, val]) => <MenuItem key={key} value={key}>{val.label}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Beantragte Summe (\u20ac)" type="number" value={form.beantragteSumme}
            onChange={(e) => setForm({...form, beantragteSumme: e.target.value})} margin="normal" />
          <TextField fullWidth label="Einreichungsfrist" type="date" value={form.einreichungsfrist}
            onChange={(e) => setForm({...form, einreichungsfrist: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
          <TextField fullWidth label="Laufzeit von" type="date" value={form.laufzeitVon}
            onChange={(e) => setForm({...form, laufzeitVon: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
          <TextField fullWidth label="Laufzeit bis" type="date" value={form.laufzeitBis}
            onChange={(e) => setForm({...form, laufzeitBis: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
          <TextField fullWidth label="Beschreibung" value={form.beschreibung}
            onChange={(e) => setForm({...form, beschreibung: e.target.value})} margin="normal" multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#1B5E20' }}>Speichern</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AntraegePage;
